import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SlotGrid from "./SlotGrid";
import { getStoredAccessCode } from "../../lib/registration";
import { participantLabel } from "../../lib/slots";
import { useToast } from "../ui/Toast";

export const PASSWORD_MIN_LENGTH = 4;
const EMPTY_FORM = { team_name: "", team_number: 1, time_slot: "", password: "" };
const JSON_HEADERS = { "Content-Type": "application/json" };

/**
 * Reservation form for one activity. Loads slot availability on mount (and whenever `refreshKey`
 * changes), posts the reservation to the activity's backend endpoint and reports the created
 * reservation through `onReserved` so the page can show the QR code.
 */
const ReservationForm = ({ activity, refreshKey = 0, onReserved }) => {
  const showToast = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [availability, setAvailability] = useState({});
  const [capacityInfo, setCapacityInfo] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadAvailability = useCallback(async () => {
    try {
      const response = await fetch(activity.api.availability);
      if (!response.ok) return;
      const data = await response.json();
      setAvailability(data.availability || {});
      setCapacityInfo(data.capacity_info || {});
    } catch (error) {
      console.error("Failed to fetch time availability:", error);
    }
  }, [activity.api.availability]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability, refreshKey]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.time_slot) {
      setErrors({ time_slot: "Vyberte prosím časový slot." });
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    try {
      const response = await fetch(activity.api.create, {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({ ...form, access_code: getStoredAccessCode() }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.field) {
          setErrors({ [data.field]: data.error });
          return;
        }
        throw new Error(data.error || "Rezervaci se nepodařilo vytvořit");
      }
      onReserved({ id: data.reservation.id, ...form, table: activity.table });
      setForm(EMPTY_FORM);
      await loadAvailability();
    } catch (error) {
      showToast(`Chyba při vytváření rezervace: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const id = (field) => `${activity.key}-${field}`;
  const teamSize = Number(form.team_number);
  const teamSizes = Array.from({ length: activity.maxTeamSize }, (_, index) => index + 1);

  return (
    <form className={`form ${activity.isMain ? "main" : ""}`.trim()} onSubmit={handleSubmit} aria-labelledby={id("title")}>
      {activity.isMain && <span className="badge">Hlavní aktivita</span>}
      <h3 id={id("title")}>{activity.title}</h3>
      <p className="desc">{activity.description}</p>

      <div className="field">
        <label htmlFor={id("team")}>
          Název týmu <small>*</small>
        </label>
        <input
          id={id("team")}
          name="team_name"
          value={form.team_name}
          onChange={(event) => updateField("team_name", event.target.value)}
          required
          placeholder="Zadejte název vašeho týmu"
          className={errors.team_name ? "invalid" : ""}
        />
        {errors.team_name && <p className="err">{errors.team_name}</p>}
      </div>

      <div className="field">
        <label htmlFor={id("password")}>
          Heslo <small>*</small>
        </label>
        <input
          id={id("password")}
          name="password"
          type="password"
          value={form.password}
          onChange={(event) => updateField("password", event.target.value)}
          required
          minLength={PASSWORD_MIN_LENGTH}
          placeholder={`Zadejte heslo (min. ${PASSWORD_MIN_LENGTH} znaky)`}
          className={errors.password ? "invalid" : ""}
        />
        {errors.password && <p className="err">{errors.password}</p>}
      </div>

      <div className="field">
        <label htmlFor={id("size")}>
          Počet účastníků <small>*</small>
        </label>
        <select
          id={id("size")}
          name="team_number"
          value={form.team_number}
          onChange={(event) => updateField("team_number", Number(event.target.value))}
          required
        >
          {teamSizes.map((size) => (
            <option key={size} value={size}>
              {size} {participantLabel(size)}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>
          Časový slot <small>*</small>
        </label>
        <SlotGrid
          slots={activity.slots}
          availability={availability}
          capacityInfo={capacityInfo}
          value={form.time_slot}
          onChange={(slot) => updateField("time_slot", slot)}
          teamSize={teamSize}
        />
        {errors.time_slot && <p className="err">{errors.time_slot}</p>}
      </div>

      <button type="submit" className={`btn ${activity.buttonClass}`} disabled={isSubmitting}>
        {isSubmitting ? "Odesílám…" : activity.cta}
      </button>
      <p className="already">
        Už máte rezervaci? <Link to="/manage">Spravovat existující rezervace</Link>
      </p>
    </form>
  );
};

export default ReservationForm;
